"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { HiGlobeAlt } from "react-icons/hi";
import { useLanguage, LANGUAGE_FLAGS } from "@/lib/i18n";
import { FaInstagram, FaTiktok, FaYoutube, FaWhatsapp } from "react-icons/fa";
import { motion, type Variants } from "framer-motion";

interface FullScreenMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const containerVariants: Variants = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
  closed: {
    opacity: 0,
    y: -40,
    transition: {
      duration: 0.2,
      ease: "easeIn",
      staggerChildren: 0.02,
      staggerDirection: -1,
    },
  },
};

const itemVariants: Variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: {
      y: { stiffness: 1000, velocity: -100 },
    },
  },
  closed: {
    y: 20,
    opacity: 0,
    transition: {
      y: { stiffness: 1000 },
    },
  },
};

export default function FullScreenMenu({
  isOpen,
  onClose,
}: FullScreenMenuProps) {
  const { language, setLanguage } = useLanguage();

  const mainLinks =
    language === "id"
      ? [
          { name: "Beranda", href: "/" },
          { name: "Kamar & Suite", href: "/rooms" },
          { name: "Fasilitas", href: "/facilities" },
          { name: "Galeri", href: "/gallery" },
        ]
      : [
          { name: "Home", href: "/" },
          { name: "Rooms & Suites", href: "/rooms" },
          { name: "Facilities", href: "/facilities" },
          { name: "Gallery", href: "/gallery" },
        ];

  const secondaryLinks =
    language === "id"
      ? [
          { name: "Tentang KinKen", href: "/about" },
          { name: "Lokasi & Peta", href: "/location" },
          { name: "Pusat FAQ", href: "/faq" },
          { name: "Hubungi Kami", href: "/contact" },
          { name: "Testimoni", href: "/testimonials" },
          { name: "Karir", href: "/career" },
        ]
      : [
          { name: "About KinKen", href: "/about" },
          { name: "Location & Map", href: "/location" },
          { name: "FAQ Center", href: "/faq" },
          { name: "Contact Us", href: "/contact" },
          { name: "Testimonials", href: "/testimonials" },
          { name: "Career", href: "/career" },
        ];

  const content =
    language === "id"
      ? {
          exploreMore: "Jelajahi Lebih Lanjut",
          languageTitle: "Bahasa",
          followKinken: "Ikuti KinKen",
          experienceText: "Rasakan hunian mewah di Bali",
          bookSanctuary: "Pesan Hunian Anda",
        }
      : {
          exploreMore: "Explore More",
          languageTitle: "Language",
          followKinken: "Follow KinKen",
          experienceText: "Experience luxury living in Bali",
          bookSanctuary: "Book Your Sanctuary",
        };

  return (
    <motion.div
      initial="closed"
      animate={isOpen ? "open" : "closed"}
      variants={containerVariants}
      className={cn(
        "fixed inset-0 bg-white z-[90] pt-24 pb-6 overflow-hidden",
        !isOpen && "pointer-events-none"
      )}
    >
      <div
        className="container px-6 md:px-12 h-full overflow-y-auto hide-scrollbar"
        style={{ maxHeight: "calc(100vh - 6rem)" }}
        data-lenis-prevent
      >
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 content-start mt-4 pb-8">
          {/* MAIN NAVIGATION */}
          <nav className="md:col-span-5 flex flex-col space-y-2 md:space-y-4">
            {mainLinks.map((item, idx) => (
              <motion.div variants={itemVariants} key={item.name}>
                <Link
                  href={item.href}
                  onClick={onClose}
                  className={cn(
                    "font-serif text-4xl md:text-6xl text-brand-charcoal transition-all duration-500 block w-fit transform",
                    "hover:text-brand-gold hover:translate-x-4"
                  )}
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* SECONDARY NAVIGATION */}
          <motion.div
            className="md:col-span-4 flex flex-col space-y-6 pt-6 md:pt-4 border-t md:border-t-0 border-gray-100 mt-6 md:mt-0 font-sans"
            variants={itemVariants}
          >
            <h4 className="text-xs font-bold text-brand-gold uppercase tracking-[0.2em] mb-2 hidden md:block">
              {content.exploreMore}
            </h4>
            <nav className="flex flex-col space-y-3">
              {secondaryLinks.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  onClick={onClose}
                  className="text-lg md:text-xl text-brand-charcoal/70 hover:text-brand-gold hover:pl-2 transition-all w-fit"
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </motion.div>

          {/* SOCIAL COLUMN */}
          <motion.div
            className="md:col-span-3 flex flex-col space-y-6 pt-4 text-left"
            variants={itemVariants}
          >
            {/* Language Switcher - Mobile */}
            <div className="md:hidden">
              <h4 className="text-xs font-bold text-brand-gold uppercase tracking-[0.2em] mb-3 font-sans">
                {content.languageTitle}
              </h4>
              <div className="flex gap-2">
                <button
                  onClick={() => setLanguage("id")}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 font-sans",
                    language === "id"
                      ? "bg-brand-gold text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  <span>{LANGUAGE_FLAGS.id}</span>
                  <span>Indonesia</span>
                </button>
                <button
                  onClick={() => setLanguage("en")}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 font-sans",
                    language === "en"
                      ? "bg-brand-gold text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  )}
                >
                  <span>{LANGUAGE_FLAGS.en}</span>
                  <span>English</span>
                </button>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold text-brand-gold uppercase tracking-[0.2em] mb-4 font-sans">
                {content.followKinken}
              </h4>
              <p className="font-serif text-xl text-brand-charcoal mb-2">
                @kinken.elite
              </p>
              <p className="text-sm text-gray-500 font-sans">
                #KinKenBali #LuxuryCoLiving
              </p>
            </div>

            <div className="pt-4">
              <p className="text-xs text-gray-500 mb-3 font-sans">
                {content.experienceText}
              </p>
              <a
                href="https://wa.me/6281916401702"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClose}
                className="inline-block text-xs font-bold uppercase tracking-widest border-b border-brand-gold text-brand-gold hover:text-brand-charcoal hover:border-brand-charcoal transition-all font-sans"
              >
                {content.bookSanctuary}
              </a>
            </div>
          </motion.div>
        </div>

        {/* FOOTER */}
        <motion.div
          className="mt-12 pt-8 border-t border-gray-100 flex flex-col-reverse md:flex-row justify-between items-center gap-6 font-sans"
          variants={itemVariants}
        >
          <div className="flex gap-6 text-[10px] md:text-xs text-gray-400 uppercase tracking-wider font-medium">
            <Link
              href="/privacy-policy"
              onClick={onClose}
              className="hover:text-brand-gold"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-of-service"
              onClick={onClose}
              className="hover:text-brand-gold"
            >
              Terms of Service
            </Link>
            <Link
              href="/sitemap"
              onClick={onClose}
              className="hover:text-brand-gold"
            >
              Sitemap
            </Link>
          </div>

          <div className="flex gap-6 text-brand-gold items-center">
            <a
              href="https://instagram.com/kinken.bali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-black hover:-translate-y-1 transition-all"
              aria-label="Instagram"
            >
              <FaInstagram size={22} />
            </a>
            <a
              href="https://tiktok.com/@kinken.bali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-black hover:-translate-y-1 transition-all"
              aria-label="TikTok"
            >
              <FaTiktok size={20} />
            </a>
            <a
              href="https://youtube.com/@kinken.bali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-black hover:-translate-y-1 transition-all"
              aria-label="YouTube"
            >
              <FaYoutube size={22} />
            </a>
            <a
              href="https://wa.me/6281916401702"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-black hover:-translate-y-1 transition-all"
              aria-label="WhatsApp"
            >
              <FaWhatsapp size={22} />
            </a>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
